import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/context/AuthContext'
import { useLeagueConfig } from '@/hooks/useLeagueConfig'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Loader2,
  Lock,
  Unlock,
  AlertTriangle,
  Coins,
  Trophy,
} from 'lucide-react'
import type {
  EconomyConfig,
  EconomyPreset,
  PresetOption,
  EconomySegment,
} from '@/types/economy'
import {
  createDefaultConfig,
  getPresetSegments,
  calculateBreakdown,
  validateConfig,
} from '@/lib/economy'
import { OwlCalculator } from './OwlCalculator'

interface EconomySetupProps {
  leagueId: string
  memberCount: number
}

const AUTOSAVE_DELAY_MS = 800

export function EconomySetup({ leagueId, memberCount }: EconomySetupProps) {
  const { isSuperAdmin, currentMembership } = useAuth()
  const { config: leagueConfig, isLoading: configLoading } = useLeagueConfig(leagueId)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [economyConfig, setEconomyConfig] = useState<EconomyConfig | null>(null)
  const [saving, setSaving] = useState(false)
  const [lockDialogOpen, setLockDialogOpen] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const dirtyRef = useRef(false)

  const canEdit =
    isSuperAdmin ||
    currentMembership?.role === 'owner' ||
    currentMembership?.role === 'admin'

  useEffect(() => {
    setInitialized(false)
    dirtyRef.current = false
  }, [leagueId])

  useEffect(() => {
    if (!configLoading && !initialized) {
      setEconomyConfig(leagueConfig.economy_config ?? null)
      setInitialized(true)
    }
  }, [configLoading, leagueConfig.economy_config, initialized])

  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!initialized || !economyConfig || economyConfig.locked) return

    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    autosaveTimer.current = setTimeout(async () => {
      if (!dirtyRef.current) return

      const errors = validateConfig(economyConfig)
      if (errors.length > 0) return

      dirtyRef.current = false
      setSaving(true)
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const { error } = await (supabase
        .from('league_configs' as any)
        .update({ economy_config: economyConfig } as any)
        .eq('league_id', leagueId) as any)
      /* eslint-enable @typescript-eslint/no-explicit-any */
      if (error) {
        dirtyRef.current = true
        toast({ title: 'Failed to save', description: error.message, variant: 'destructive' })
      } else {
        queryClient.invalidateQueries({ queryKey: ['league-config'] })
      }
      setSaving(false)
    }, AUTOSAVE_DELAY_MS)

    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    }
  }, [economyConfig, initialized, leagueId, toast, queryClient])

  const handleEnableEconomy = async () => {
    const config = createDefaultConfig()
    setEconomyConfig(config)
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const { error } = await (supabase
      .from('league_configs' as any)
      .update({ economy_config: config } as any)
      .eq('league_id', leagueId) as any)
    /* eslint-enable @typescript-eslint/no-explicit-any */
    if (error) {
      toast({ title: 'Failed to enable economy', description: error.message, variant: 'destructive' })
      setEconomyConfig(null)
    } else {
      queryClient.invalidateQueries({ queryKey: ['league-config'] })
    }
  }

  const handleDisableEconomy = async () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    dirtyRef.current = false
    const prev = economyConfig
    setEconomyConfig(null)
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const { error } = await (supabase
      .from('league_configs' as any)
      .update({ economy_config: null } as any)
      .eq('league_id', leagueId) as any)
    /* eslint-enable @typescript-eslint/no-explicit-any */
    if (error) {
      toast({ title: 'Failed to disable economy', description: error.message, variant: 'destructive' })
      setEconomyConfig(prev)
    } else {
      toast({ title: 'Owl economy disabled' })
      queryClient.invalidateQueries({ queryKey: ['league-config'] })
    }
  }

  const updateConfig = useCallback((updates: Partial<EconomyConfig>) => {
    setEconomyConfig((prev) => (prev ? { ...prev, ...updates } : null))
    dirtyRef.current = true
  }, [])

  const handlePresetChange = (preset: EconomyPreset) => {
    if (!economyConfig) return
    const option: PresetOption =
      preset === 'end_focused_simplified' ? 'end_only' : null
    updateConfig({
      preset,
      preset_option: option,
      segments: getPresetSegments(preset, option),
    })
  }

  const handlePresetOptionChange = (option: PresetOption) => {
    if (!economyConfig) return
    updateConfig({
      preset_option: option,
      segments: getPresetSegments(economyConfig.preset, option),
    })
  }

  const handleSegmentPercentageChange = (key: string, value: number) => {
    if (!economyConfig) return
    const segments = economyConfig.segments.map((s) =>
      s.key === key ? { ...s, pot_percentage: value } : s,
    )
    updateConfig({ segments })
  }

  const handleRankPercentageChange = (
    segmentKey: string,
    place: number,
    value: number,
  ) => {
    if (!economyConfig) return
    const segments = economyConfig.segments.map((s) => {
      if (s.key !== segmentKey) return s
      return {
        ...s,
        ranks: s.ranks.map((r) =>
          r.place === place ? { ...r, percentage: value } : r,
        ),
      }
    })
    updateConfig({ segments })
  }

  const handleSegmentToggle = (key: string, enabled: boolean) => {
    if (!economyConfig) return
    const defaultRanks = [
      { place: 1, percentage: 50 },
      { place: 2, percentage: 30 },
      { place: 3, percentage: 20 },
    ]
    const segments = economyConfig.segments.map((s) => {
      if (s.key !== key) return s
      const ranks = enabled && s.ranks.length === 0 ? defaultRanks : s.ranks
      return { ...s, enabled, ranks }
    })
    updateConfig({ segments })
  }

  const handleLock = async () => {
    if (!economyConfig) return
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    dirtyRef.current = false
    const locked = { ...economyConfig, locked: true, locked_at: new Date().toISOString() }
    setEconomyConfig(locked)
    setSaving(true)
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const { error } = await (supabase
      .from('league_configs' as any)
      .update({ economy_config: locked } as any)
      .eq('league_id', leagueId) as any)
    /* eslint-enable @typescript-eslint/no-explicit-any */

    if (error) {
      toast({ title: 'Failed to lock', description: error.message, variant: 'destructive' })
      setEconomyConfig((prev) => (prev ? { ...prev, locked: false, locked_at: null } : null))
      dirtyRef.current = true
    } else {
      toast({ title: 'League economics locked' })
      dirtyRef.current = false
      queryClient.invalidateQueries({ queryKey: ['league-config'] })
    }
    setSaving(false)
    setLockDialogOpen(false)
  }

  const handleUnlock = async () => {
    if (!economyConfig) return
    const unlocked = { ...economyConfig, locked: false, locked_at: null }
    setEconomyConfig(unlocked)
    setSaving(true)
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const { error } = await (supabase
      .from('league_configs' as any)
      .update({ economy_config: unlocked } as any)
      .eq('league_id', leagueId) as any)
    /* eslint-enable @typescript-eslint/no-explicit-any */

    if (error) {
      toast({ title: 'Failed to unlock', description: error.message, variant: 'destructive' })
      setEconomyConfig((prev) => (prev ? { ...prev, locked: true, locked_at: economyConfig.locked_at } : null))
    } else {
      toast({ title: 'League economics unlocked' })
      queryClient.invalidateQueries({ queryKey: ['league-config'] })
    }
    setSaving(false)
  }

  if (configLoading || !initialized) {
    return (
      <Card>
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        </CardContent>
      </Card>
    )
  }

  // Economy not configured yet
  if (!economyConfig) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Owl Economy
          </CardTitle>
          <CardDescription>
            Set up a virtual economy to manage buy-ins and payouts for your league.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {canEdit ? (
            <Button onClick={handleEnableEconomy}>
              <Coins className="h-4 w-4 mr-2" />
              Enable Owl Economy
            </Button>
          ) : (
            <p className="text-sm text-stone-500">
              Economy has not been configured for this league.
            </p>
          )}
        </CardContent>
      </Card>
    )
  }

  const validationErrors = validateConfig(economyConfig)
  const breakdown = calculateBreakdown(economyConfig, memberCount)
  const isLocked = economyConfig.locked

  return (
    <div className="space-y-6">
      {/* Header + Lock Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5" />
                Owl Economy
                {isLocked && (
                  <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                    <Lock className="h-3 w-3 mr-1" />
                    Locked
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Configure buy-in and payout structure
              </CardDescription>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                {!isLocked && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDisableEconomy}
                    className="text-red-600 hover:text-red-700"
                  >
                    Disable
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Buy-in */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Buy-in</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div className="space-y-2 flex-1 max-w-xs">
              <Label htmlFor="buyIn">Amount per player (owls)</Label>
              <Input
                id="buyIn"
                type="number"
                min="1"
                value={economyConfig.buy_in}
                onChange={(e) => updateConfig({ buy_in: parseInt(e.target.value) || 0 })}
                disabled={isLocked || !canEdit}
              />
            </div>
            <div className="text-sm text-stone-500 pb-2">
              {memberCount} players = <span className="font-semibold text-stone-900 dark:text-stone-100">{breakdown.totalPot} owls</span> total pot
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preset Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Payout Preset</CardTitle>
          <CardDescription>Choose a base structure, then customize below</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3
                          sm:grid-cols-2">
            <button
              onClick={() => !isLocked && canEdit && handlePresetChange('balanced_segmented')}
              disabled={isLocked || !canEdit}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                economyConfig.preset === 'balanced_segmented'
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20'
                  : 'border-stone-200 dark:border-stone-700 hover:border-stone-300'
              } ${isLocked || !canEdit ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="font-semibold text-sm">Balanced Segmented</div>
              <p className="text-xs text-stone-500 mt-1">
                1H + 2H + Overall payouts. Multiple payout moments for high engagement.
              </p>
            </button>
            <button
              onClick={() => !isLocked && canEdit && handlePresetChange('end_focused_simplified')}
              disabled={isLocked || !canEdit}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                economyConfig.preset === 'end_focused_simplified'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                  : 'border-stone-200 dark:border-stone-700 hover:border-stone-300'
              } ${isLocked || !canEdit ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="font-semibold text-sm">End-Focused Simplified</div>
              <p className="text-xs text-stone-500 mt-1">
                Overall only or 2H + Overall. Simple leagues with a strong season-end climax.
              </p>
            </button>
          </div>

          {economyConfig.preset === 'end_focused_simplified' && (
            <div className="space-y-2">
              <Label>Option</Label>
              <Select
                value={economyConfig.preset_option || 'end_only'}
                onValueChange={(v) => handlePresetOptionChange(v as PresetOption)}
                disabled={isLocked || !canEdit}
              >
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="end_only">Overall Only (100%)</SelectItem>
                  <SelectItem value="hybrid">Hybrid — 2H (30%) + Overall (70%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Segment Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Segments & Payouts
          </CardTitle>
          <CardDescription>Adjust how the pot is split across segments and ranks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {economyConfig.segments.map((segment) => {
            const segBreakdown = breakdown.segments.find((s) => s.key === segment.key)
            return (
            <SegmentEditor
              key={segment.key}
              segment={segment}
              correctedOwls={segBreakdown?.owls ?? 0}
              correctedRankOwls={segBreakdown?.ranks}
              disabled={isLocked || !canEdit}
              onToggle={(enabled) => handleSegmentToggle(segment.key, enabled)}
              onPercentageChange={(value) =>
                handleSegmentPercentageChange(segment.key, value)
              }
              onRankChange={(place, value) =>
                handleRankPercentageChange(segment.key, place, value)
              }
            />
            )
          })}
        </CardContent>
      </Card>

      {/* Live Calculation */}
      <OwlCalculator breakdown={breakdown} validationErrors={validationErrors} />

      {/* Actions */}
      {canEdit && (
        <Card>
          <CardContent className="pt-6">
            {validationErrors.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm font-medium mb-1">
                  <AlertTriangle className="h-4 w-4" />
                  Unsaved — fix errors to save
                </div>
                <ul className="text-xs text-red-600 dark:text-red-400 space-y-1">
                  {validationErrors.map((e, i) => (
                    <li key={i}>- {e.message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-3">
              {!isLocked ? (
                <Button
                  variant="outline"
                  onClick={() => setLockDialogOpen(true)}
                  disabled={saving || validationErrors.length > 0}
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Lock League Economics
                </Button>
              ) : (
                <Button variant="outline" onClick={handleUnlock} disabled={saving}>
                  <Unlock className="h-4 w-4 mr-2" />
                  Unlock
                </Button>
              )}
              {saving && (
                <span className="flex items-center gap-1.5 text-xs text-stone-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving...
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lock Confirmation Dialog */}
      <AlertDialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lock League Economics?</AlertDialogTitle>
            <AlertDialogDescription>
              This locks the payout structure for the season. You can unlock later if needed, but
              changes after the season starts may affect player expectations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4 p-3 rounded-lg bg-stone-50 dark:bg-stone-900 text-sm space-y-1">
            <div>Total Pot: <strong>{breakdown.totalPot} owls</strong></div>
            <div>Buy-in: <strong>{breakdown.buyIn} owls/player</strong></div>
            <div>Players: <strong>{breakdown.playerCount}</strong></div>
            {breakdown.segments.map((seg) => (
              <div key={seg.key}>
                {seg.label}: <strong>{seg.owls} owls ({seg.percentage}%)</strong>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLock}>
              <Lock className="h-4 w-4 mr-2" />
              Lock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Segment Editor Sub-component
// ---------------------------------------------------------------------------

function SegmentEditor({
  segment,
  correctedOwls,
  correctedRankOwls,
  disabled,
  onToggle,
  onPercentageChange,
  onRankChange,
}: {
  segment: EconomySegment
  correctedOwls: number
  correctedRankOwls?: { place: number; owls: number; percentage: number }[]
  disabled: boolean
  onToggle: (enabled: boolean) => void
  onPercentageChange: (value: number) => void
  onRankChange: (place: number, value: number) => void
}) {
  const segmentOwls = segment.enabled ? correctedOwls : 0

  return (
    <div
      className={`rounded-lg border p-4 space-y-3 transition-opacity ${
        segment.enabled
          ? 'border-stone-200 dark:border-stone-700'
          : 'border-stone-100 dark:border-stone-800 opacity-50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Switch
            checked={segment.enabled}
            onCheckedChange={onToggle}
            disabled={disabled || segment.key === 'overall'}
          />
          <span className="font-medium text-sm">{segment.label}</span>
          {segment.enabled && (
            <Badge variant="secondary" className="text-xs">
              {segmentOwls} owls
            </Badge>
          )}
        </div>
      </div>

      {segment.enabled && (
        <>
          <div className="flex items-end gap-3">
            <div className="space-y-1 w-32">
              <Label className="text-xs">% of Pot</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={segment.pot_percentage}
                onChange={(e) => onPercentageChange(parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-stone-500">Rank Distribution</Label>
            <div className="grid grid-cols-3 gap-2">
              {segment.ranks.map((rank) => {
                const correctedRank = correctedRankOwls?.find((r) => r.place === rank.place)
                const rankOwls = correctedRank?.owls ?? Math.floor(segmentOwls * (rank.percentage / 100))
                return (
                  <div key={rank.place} className="space-y-1">
                    <Label className="text-xs">
                      {rank.place === 1 ? '1st' : rank.place === 2 ? '2nd' : '3rd'}
                    </Label>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={rank.percentage}
                        onChange={(e) =>
                          onRankChange(rank.place, parseFloat(e.target.value) || 0)
                        }
                        disabled={disabled}
                        className="h-8 text-sm w-20"
                      />
                      <span className="text-xs text-stone-400">%</span>
                    </div>
                    <div className="text-xs text-stone-500">{rankOwls} owls</div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
